/**
 * The service worker on the page (part 40a, table U): registering it, whether the app is ready to start offline, and the
 * update flow. A new build waits until the writer says "Update now" (principle 3); then, in every tab, the open chapter's
 * typing is stored before the page reloads, and a tab whose typing can't be stored stays as it is (principle 1).
 *
 * The browser's service worker container, the workspace's `settle` and the page's reload are injected, so the logic
 * runs in tests.
 */
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';

/** Whether the app can start with no network (R′1–R′3). */
export type OfflineState = 'unavailable' | 'preparing' | 'ready';

/** Where an update stands: none, one waiting for the writer, under way (activating or storing typing), or stopped (U6). */
export type UpdateState = 'none' | 'ready' | 'updating' | 'failed';

/** A service worker, as the page drives it (the browser's `ServiceWorker`). */
export interface ServiceWorkerLike {
  readonly state: string;
  postMessage(message: unknown): void;
  addEventListener(type: 'statechange', listener: () => void): void;
}

/** A registration (the browser's `ServiceWorkerRegistration`). */
export interface RegistrationLike {
  readonly installing: ServiceWorkerLike | null;
  readonly waiting: ServiceWorkerLike | null;
  readonly active: ServiceWorkerLike | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
  update(): Promise<unknown>;
}

/** The page's service worker container (the browser's `navigator.serviceWorker`). */
export interface ContainerLike {
  /** The worker controlling this page, or null. */
  readonly controller: unknown;
  register(url: string, options: RegistrationOptions): Promise<RegistrationLike>;
  addEventListener(type: 'controllerchange', listener: () => void): void;
  removeEventListener(type: 'controllerchange', listener: () => void): void;
}

/**
 * U1: whether `value` is a service worker container: a browser may name `navigator.serviceWorker` and hold nothing there
 * (WebKit with workers turned off), and the app must start all the same (principle 4).
 */
export function isContainer(value: unknown): value is ContainerLike {
  if (typeof value !== 'object' || value === null) return false;
  return ['register', 'addEventListener', 'removeEventListener'].every((name) => typeof Reflect.get(value, name) === 'function');
}

/**
 * U3: whether what registering answered can be checked for updates (U10, which runs outside the registration's promise);
 * anything else that isn't a registration throws while it is followed, which the same promise catches.
 */
function isRegistration(value: unknown): value is RegistrationLike {
  return typeof Reflect.get(Object(value), 'update') === 'function';
}

/** What the controller needs; everything with an effect is injected. */
export interface PwaOptions {
  /** The container; undefined without service workers (U1). */
  readonly container: ContainerLike | undefined;
  /** The worker script's URL as the app's Trusted Types policy hands it out; null on the dev server (U1). */
  readonly url: (() => string) | null;
  /** Stores the open chapter's typing (the workspace's `settle`, table N); true once stored. Never rejects. */
  readonly settle: () => Promise<boolean>;
  readonly reload: () => void;
  /** Milliseconds, for U10. */
  readonly now: () => number;
}

/** U10: how long after one update check the next may run. */
export const UPDATE_CHECK_MS = 60 * 60 * 1000;

/** M1: what tells a waiting worker to activate. */
export const ACTIVATE = Object.freeze({ kind: 'activate' });

/** The service worker's state on the page and the update flow. */
export class PwaController {
  readonly offline: Accessor<OfflineState>;
  readonly update: Accessor<UpdateState>;
  readonly #setOffline: (state: OfflineState) => void;
  readonly #setUpdate: (state: UpdateState) => void;
  readonly #options: PwaOptions;
  #registration: RegistrationLike | null = null;
  /** Whether a worker controls the page (U6, U7): from the start, or since a first one took it. */
  #controlled = false;
  /** Whether this tab asked for the update (U5, U9, U11). */
  #asked = false;
  /** Whether the typing is being stored for a reload (U12). */
  #settling = false;
  /** The worker told to activate (U8). */
  #told: ServiceWorkerLike | null = null;
  #checked = 0;
  readonly #watched = new WeakSet<ServiceWorkerLike>();
  readonly #changed = (): void => {
    this.#controllerChanged();
  };

  constructor(options: PwaOptions) {
    this.#options = options;
    const [offline, setOffline] = createSignal<OfflineState>('preparing');
    const [update, setUpdate] = createSignal<UpdateState>('none');
    this.offline = offline;
    this.update = update;
    this.#setOffline = setOffline;
    this.#setUpdate = setUpdate;
  }

  /**
   * U1–U4: registers the worker and follows it; offline `unavailable` without one, when registering fails, or when the
   * container throws (principle 4: the app starts all the same).
   */
  start(): void {
    const { container, url } = this.#options;
    if (container === undefined || url === null) {
      this.#setOffline('unavailable');
      return;
    }
    try {
      this.#controlled = container.controller !== null;
      container.addEventListener('controllerchange', this.#changed);
    } catch {
      this.#setOffline('unavailable');
      return;
    }
    this.#checked = this.#options.now();
    // The URL is made here, inside the promise: a policy that refuses it is a registration that failed (U3).
    Promise.resolve()
      .then(() => container.register(url(), { scope: '/', updateViaCache: 'none' }))
      .then((registration: unknown) => {
        // U3: a browser may answer with nothing (WebKit with workers blocked).
        if (!isRegistration(registration)) {
          this.#setOffline('unavailable');
          return;
        }
        registration.addEventListener('updatefound', () => {
          this.#watch(registration, registration.installing);
        });
        this.#watch(registration, registration.installing);
        this.#watch(registration, registration.waiting);
        this.#registration = registration;
        this.#refresh(registration);
      })
      .catch(() => {
        this.#setOffline('unavailable');
      });
  }

  /** Stops following the controller (the app closing, in tests): a later change reloads nothing. */
  stop(): void {
    this.#options.container?.removeEventListener('controllerchange', this.#changed);
  }

  /** U5, U9, and U6's "Try again": the waiting build takes over, and the page reloads once the typing is stored. */
  applyUpdate(): void {
    const state = this.update();
    if (state === 'failed') {
      this.#settleAndReload();
      return;
    }
    const registration = this.#registration;
    // `ready` only ever comes from a registration's workers (#refresh), so it never comes without one.
    if (state !== 'ready' || registration === null) return;
    this.#asked = true;
    this.#setUpdate('updating');
    const { waiting } = registration;
    if (waiting === null) this.#settleAndReload();
    else this.#tell(waiting);
  }

  /** U11: the outdated screen's Reload: through the update when one is waiting or stopped, else a plain reload. */
  restart(): void {
    if (this.update() === 'none') this.#options.reload();
    else this.applyUpdate();
  }

  /** U10: the page is visible again: an update check, at most once an hour; one that fails is ignored. */
  visible(): void {
    const registration = this.#registration;
    const now = this.#options.now();
    if (registration === null || now - this.#checked < UPDATE_CHECK_MS) return;
    this.#checked = now;
    registration.update().catch(() => undefined);
  }

  /** Follows a worker of the registration's states, once each. */
  #watch(registration: RegistrationLike, worker: ServiceWorkerLike | null): void {
    if (worker === null || this.#watched.has(worker)) return;
    this.#watched.add(worker);
    worker.addEventListener('statechange', () => {
      this.#refresh(registration);
    });
  }

  /** M1: tells a waiting worker to activate (U5, U8). */
  #tell(worker: ServiceWorkerLike): void {
    // A waiting worker is already followed: it was installing (or waiting) when the registration was first followed, or
    // since `updatefound`.
    this.#told = worker;
    worker.postMessage(ACTIVATE);
  }

  /** U2, U4, U8: the offline state and the update from the registration's workers now. */
  #refresh(registration: RegistrationLike): void {
    const { waiting, active } = registration;
    this.#setOffline(active === null ? 'preparing' : 'ready');
    const state = this.update();
    if (state === 'none' && waiting !== null && active !== null) this.#setUpdate('ready');
    // Another tab took the waiting build: this one follows when its controller changes (U6), or has nothing to do (U7).
    if (state === 'ready' && waiting === null) this.#setUpdate('none');
    // U8: the told worker was replaced by a newer build before it activated.
    if (state === 'updating' && !this.#settling && this.#told?.state === 'redundant') {
      if (waiting === null) this.#setUpdate('none');
      else this.#tell(waiting);
    }
  }

  /** U6, U7: a new worker controls the page. */
  #controllerChanged(): void {
    if (!this.#controlled && !this.#asked) {
      this.#controlled = true;
      return;
    }
    this.#settleAndReload();
  }

  /** U6, U12, table N: the typing stored, then the reload; not stored, the page stays and says so. */
  #settleAndReload(): void {
    if (this.#settling) return;
    this.#settling = true;
    this.#setUpdate('updating');
    void this.#options.settle().then((stored) => {
      if (stored) {
        this.#options.reload();
        return;
      }
      this.#settling = false;
      this.#setUpdate('failed');
    });
  }
}
